import { SupabaseClient } from '@supabase/supabase-js'

export interface StandardSkill {
  id: string
  name: string
  category: string | null
}

/**
 * 스킬 검색 함수
 * 공백 및 특수문자를 제거한 상태에서 검색어와 매칭합니다.
 * 
 * @param supabase Supabase 클라이언트
 * @param query 검색어 (2글자 이상)
 * @param category 카테고리 필터 (선택)
 * @param limit 최대 결과 수 (기본 20개)
 * @returns 검색된 스킬 목록
 */
export async function searchSkills(
  supabase: SupabaseClient,
  query: string,
  category?: string | null,
  limit: number = 20
): Promise<StandardSkill[]> {
  const trimmedQuery = query.trim()
  
  // 2글자 미만이면 빈 배열 반환
  if (trimmedQuery.length < 2) {
    return []
  }

  // 검색어에서 공백 및 특수문자 제거 및 소문자 변환
  const normalizedQuery = normalizeSearchQuery(trimmedQuery)

  try {
    let queryBuilder = supabase
      .from('standard_skills')
      .select('id, name, category')
      .ilike('name_normalized', `%${normalizedQuery}%`)
      .limit(limit)
      .order('name', { ascending: true })

    // 카테고리 필터 적용
    if (category) {
      queryBuilder = queryBuilder.eq('category', category)
    }

    const { data, error } = await queryBuilder

    if (error) {
      console.error('[searchSkills] 검색 오류:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[searchSkills] 예외 발생:', error)
    return []
  }
}

/**
 * 카테고리별 스킬 목록 조회
 * 
 * @param supabase Supabase 클라이언트
 * @param category 카테고리
 * @returns 카테고리에 속한 스킬 목록
 */
export async function getSkillsByCategory(
  supabase: SupabaseClient,
  category: string
): Promise<StandardSkill[]> {
  try {
    const { data, error } = await supabase
      .from('standard_skills')
      .select('id, name, category')
      .eq('category', category)
      .order('name', { ascending: true })

    if (error) {
      console.error('[getSkillsByCategory] 조회 오류:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[getSkillsByCategory] 예외 발생:', error)
    return []
  }
}

/**
 * 여러 스킬 ID로 스킬 정보 조회
 * 
 * @param supabase Supabase 클라이언트
 * @param skillIds 스킬 ID 배열
 * @returns 스킬 정보 배열
 */
export async function getSkillsByIds(
  supabase: SupabaseClient,
  skillIds: string[]
): Promise<StandardSkill[]> {
  if (skillIds.length === 0) {
    return []
  }

  try {
    const { data, error } = await supabase
      .from('standard_skills')
      .select('id, name, category')
      .in('id', skillIds)
      .order('name', { ascending: true })

    if (error) {
      console.error('[getSkillsByIds] 조회 오류:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('[getSkillsByIds] 예외 발생:', error)
    return []
  }
}

/**
 * 스킬명으로 스킬 정보 조회
 * 
 * @param supabase Supabase 클라이언트
 * @param skillName 스킬명
 * @returns 스킬 정보 또는 null
 */
export async function getSkillByName(
  supabase: SupabaseClient,
  skillName: string
): Promise<StandardSkill | null> {
  try {
    const { data, error } = await supabase
      .from('standard_skills')
      .select('id, name, category')
      .eq('name', skillName)
      .maybeSingle()

    if (error) {
      console.error('[getSkillByName] 조회 오류:', error)
      return null
    }

    return data || null
  } catch (error) {
    console.error('[getSkillByName] 예외 발생:', error)
    return null
  }
}

/**
 * 검색어 정규화 (공백 및 특수문자 제거)
 * 
 * @param query 검색어
 * @returns 정규화된 검색어
 */
export function normalizeSearchQuery(query: string): string {
  return query.replace(/[^a-z0-9가-힣]/gi, '').toLowerCase()
}

